import { useEffect, useState } from 'react';

import { loadGoogleMaps } from '../services/loadGoogleMaps';
import { Geocode, Prediction } from '../types';

type AutocompleteService = google.maps.places.AutocompleteService;
type Geocoder = google.maps.Geocoder;

export const useGoogleMaps = () => {
  const [geocoder, setGeocoder] = useState<Geocoder | null>(null);
  const [autocomplete, setAutocomplete] = useState<AutocompleteService | null>(null);

  useEffect(() => {
    // Create clean up function to remove the script from the DOM if already added
    return () => {
      const script = existingScript();
      if (script) {
        script.remove();
      }
    };
  }, []);

  const existingScript = () => document.querySelector('#google-maps');

  const load = async (apiKey: string, language = 'es') => {
    await loadGoogleMaps(apiKey, language);
    await initializeServices();
  };

  const initializeServices = async () => {
    if (!google) {
      return console.error('Google not found');
    }

    const { AutocompleteService } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

    const { Geocoder } = (await google.maps.importLibrary('geocoding')) as google.maps.GeocodingLibrary;

    setAutocomplete(new AutocompleteService());
    setGeocoder(new Geocoder());
  };

  const retrievePredictions = async (input: string): Promise<Prediction[]> => {
    return new Promise<Prediction[]>((resolve, reject) => {
      if (!autocomplete) {
        return reject(new Error('Autocomplete service not found'));
      }

      const predictionsInput = {
        input,
        types: ['address'],
      };

      autocomplete.getPlacePredictions(predictionsInput, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          const matches: Prediction[] = [];
          predictions.forEach((prediction) => {
            const { types } = prediction;
            if (
              types &&
              (types.includes('street_address') ||
                types.includes('premise') ||
                types.includes('subpremise') ||
                types.includes('route'))
            ) {
              matches.push(prediction);
            }
          });
          if (matches.length === 0) {
            return reject(new Error('No predictions found after filtering'));
          }
          return resolve(matches);
        } else {
          return reject(new Error('No predictions found'));
        }
      });
    });
  };

  const geocodePlace = async (placeId: string): Promise<Geocode> => {
    return new Promise<google.maps.GeocoderResult>((resolve, reject) => {
      if (!geocoder) {
        return reject(new Error('Geocoder service not found'));
      }

      geocoder.geocode({ placeId }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results) {
          return resolve(results[0]);
        } else {
          return reject(new Error('No results found'));
        }
      });
    });
  };

  return {
    load,
    retrievePredictions,
    geocodePlace,
  };
};
